import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const HERO_BG = "https://images.unsplash.com/photo-1709377195538-5522ed0f9e10?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxnb2xkJTIwbGlxdWlkJTIwYWJzdHJhY3QlMjBkYXJrfGVufDB8fHx8MTc4NzY0NzU5NXww&ixlib=rb-4.1.0&q=85";

const MaskedLine = ({ children, delay, className }) => (
  <span className="block overflow-hidden">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center justify-center overflow-hidden grain-overlay" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img src={HERO_BG} alt="" className="w-full h-[120%] object-cover" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-transparent to-[#050505]" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative text-center px-6 pt-20">
        <MaskedLine delay={0.3}>
          <span className="font-script-lux text-gold text-3xl sm:text-4xl block mb-2" data-testid="hero-script-accent">
            Parfums d'Exception
          </span>
        </MaskedLine>

        <h1 className="font-serif-lux font-light text-white leading-[0.95]" data-testid="hero-title">
          <MaskedLine delay={0.5} className="text-6xl sm:text-8xl lg:text-9xl tracking-[0.12em]">
            MAISON
          </MaskedLine>
          <MaskedLine delay={0.7} className="text-6xl sm:text-8xl lg:text-9xl tracking-[0.12em] text-gold-gradient">
            ÉLIXIR
          </MaskedLine>
        </h1>

        <MaskedLine delay={1.0}>
          <span className="block mt-8 text-neutral-400 text-sm sm:text-base font-light tracking-[0.3em] uppercase max-w-xl mx-auto">
            El arte de la perfumería, entregado en tu puerta
          </span>
        </MaskedLine>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.4, ease: "easeOut" }}
          className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-5"
        >
          <button
            data-testid="hero-discover-button"
            onClick={() => document.getElementById("coleccion")?.scrollIntoView({ behavior: "smooth" })}
            className="group relative bg-gold text-black text-xs uppercase tracking-[0.3em] px-10 py-4 overflow-hidden"
          >
            <span className="relative z-10 transition-colors duration-500 group-hover:text-gold">Descubrir la colección</span>
            <span className="absolute inset-0 bg-black translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />
          </button>
          <button
            data-testid="hero-story-button"
            onClick={() => document.getElementById("nosotros")?.scrollIntoView({ behavior: "smooth" })}
            className="text-xs uppercase tracking-[0.3em] text-white border border-white/25 px-10 py-4 hover:border-gold hover:text-gold transition-colors duration-500"
          >
            Nuestra historia
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2, duration: 1 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3"
      >
        <span className="text-[9px] uppercase tracking-[0.4em] text-neutral-500">Desliza</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }} className="w-px h-10 bg-gradient-to-b from-gold to-transparent" />
      </motion.div>
    </section>
  );
};
